// =====================================================================
// 假行 / JiaFlight — API 端点封装
// 按业务模块组织，前后端契约统一
// =====================================================================

import { http } from './client';
import type {
  UserProfile,
  MonitorRule,
  MonitorRuleCity,
  Notification,
  DestType,
  TripType,
  ExtraDepWindow,
} from '@jiaflight/shared';

// ========== Auth ==========

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; email: string };
}

export interface MeResponse {
  id: string;
  email: string;
  isAdmin: boolean;
  profile: UserProfile;
}

export const authApi = {
  register: (email: string, password: string) =>
    http.post<{ user: { id: string; email: string }; message: string }>(
      '/api/auth/register',
      { email, password },
      { skipAuth: true },
    ),

  login: (email: string, password: string) =>
    http.post<LoginResponse>('/api/auth/login', { email, password }, { skipAuth: true }),

  me: () => http.get<MeResponse>('/api/auth/me'),

  updateProfile: (data: Partial<UserProfile>) =>
    http.put<UserProfile>('/api/auth/profile', data),

  forgotPassword: (email: string) =>
    http.post<{ message: string }>(
      '/api/auth/forgot-password',
      { email },
      { skipAuth: true },
    ),

  resetPassword: (accessToken: string, password: string) =>
    http.post<{ user: { id: string; email: string }; message: string }>(
      '/api/auth/reset-password',
      { accessToken, password },
      { skipAuth: true },
    ),
};

// ========== Monitor ==========

export interface MonitorConfig extends MonitorRule {
  cities: MonitorRuleCity[];
}

export interface CreateMonitorParams {
  departure: string;
  destLabel: string;
  destType: DestType;
  tripType?: TripType;
  maxPrice?: number | null;
  /** 规则级别覆盖：最多请假天数，null/undefined 时用全局设置 */
  maxLeave?: number | null;
  /** 规则级别覆盖：最晚起飞时间 'HH:MM'，null/undefined 时用全局设置 */
  latestDep?: string | null;
  /** 规则级别覆盖：额外出发时段，null/undefined 时用全局设置 */
  extraDepWindows?: ExtraDepWindow[] | null;
  /** 是否邮件通知（默认 true），false 时仅入库不发邮件 */
  notifyEmail?: boolean;
  enabled?: boolean;
  cities?: Array<{ cityCode: string; cityName: string; hotScore?: number }>;
}

export interface SuggestResponse {
  restPeriod: {
    start: string;
    end: string;
    type: 'weekend' | 'holiday' | 'long_holiday';
    name: string;
  };
  suggestions: Array<{
    id: string;
    leaveDays: number;
    totalDays: number;
    outbound: Array<{
      direction: 'outbound' | 'return';
      start: string;
      end: string;
      leaveDays: number;
      description: string;
    }>;
    return: Array<{
      direction: 'outbound' | 'return';
      start: string;
      end: string;
      leaveDays: number;
      description: string;
    }>;
    restType: 'weekend' | 'holiday' | 'long_holiday';
    summary: string;
  }>;
}

export const monitorApi = {
  listConfigs: () => http.get<MonitorConfig[]>('/api/monitor/configs'),

  getConfig: (id: string) => http.get<MonitorConfig>(`/api/monitor/configs/${id}`),

  createConfig: (data: CreateMonitorParams) =>
    http.post<MonitorRule>('/api/monitor/configs', data),

  updateConfig: (id: string, data: Partial<CreateMonitorParams>) =>
    http.put<MonitorRule>(`/api/monitor/configs/${id}`, data),

  deleteConfig: (id: string) => http.delete<{ deleted: boolean }>(`/api/monitor/configs/${id}`),

  suggest: (referenceDate?: string) =>
    http.get<SuggestResponse>(
      '/api/monitor/suggest' + (referenceDate ? `?referenceDate=${referenceDate}` : ''),
    ),

  scan: (ruleId: string) =>
    http.post<{
      cooldown?: boolean;
      remainingSec?: number;
      lastScannedAt?: string;
      results: Array<{
        cityCode: string;
        cityName: string;
        found: boolean;
        lowestPrice: number | null;
        airline: string | null;
        departureAt: string | null;
        leaveDays: number | null;
        isInRestPeriod: boolean;
        hitsTarget: boolean;
        source: string | null;
        bookingLink: string | null;
        returnDepartureAt: string | null;
        returnAirline: string | null;
        returnPrice: number | null;
      }>;
      summary: { total: number; found: number; hitTarget: number };
      timeWindows: {
        restPeriod: {
          name: string;
          type: string;
          start: string;
          end: string;
        };
        depDates: string[];
        maxLeave: number;
        latestDep: string;
        extraDepWindows: ExtraDepWindow[] | null;
        workEnd: string;
        commuteMin: number;
      } | null;
    }>(`/api/monitor/scan/${ruleId}`),

  /** 获取每条规则最近一次的查价结果（从 price_records）+ 下次自动查价时间 */
  latestPrices: () =>
    http.get<Record<string, {
      results: Array<{
        cityCode: string;
        cityName: string;
        found: boolean;
        lowestPrice: number | null;
        airline: string | null;
        departureAt: string | null;
        leaveDays: number | null;
        isInRestPeriod: boolean;
        hitsTarget: boolean;
        source: string | null;
        bookingLink: string | null;
        returnDepartureAt: string | null;
        returnAirline: string | null;
        returnPrice: number | null;
      }>;
      lastScannedAt: string | null;
      nextScanAt: string;
    }>>('/api/monitor/latest-prices'),
};

// ========== Flights ==========

export const flightsApi = {
  search: (origin: string, destination: string, departureAt?: string) => {
    const params = new URLSearchParams({ origin, destination });
    if (departureAt) params.set('departureAt', departureAt);
    return http.get<unknown[]>(`/api/flights/search?${params}`);
  },

  history: (cityCode: string, ruleId?: string, limit = 50, offset = 0) => {
    const params = new URLSearchParams({ cityCode, limit: String(limit), offset: String(offset) });
    if (ruleId) params.set('ruleId', ruleId);
    return http.get<unknown[]>(`/api/flights/history?${params}`);
  },
};

// ========== Cities ==========

export interface CityDictItem {
  cityCode: string;
  cityName: string;
  country: string;
  hotScore: number;
}

export const citiesApi = {
  search: (q: string, country?: string, limit = 20) => {
    const params = new URLSearchParams({ q, limit: String(limit) });
    if (country) params.set('country', country);
    return http.get<CityDictItem[]>(`/api/cities/search?${params}`, { skipAuth: true });
  },

  countries: () => http.get<string[]>('/api/cities/countries', { skipAuth: true }),
};

// ========== Notifications ==========

export const notificationsApi = {
  list: (limit = 20, offset = 0) =>
    http.get<Notification[]>(
      `/api/notifications?limit=${limit}&offset=${offset}`,
    ),
};

// ========== Recommend ==========

export interface RecommendResult {
  departure: string;
  destLabel: string;
  destType: string;
  tripType: string;
  price: number | null;
  airline: string | null;
  departureAt: string | null;
  leaveDays: number | null;
  planLabel: string;
  restPeriodId: string;
  restPeriodName: string;
  restPeriodStart: string;
  restPeriodEnd: string;
  source: string | null;
  bookingLink: string | null;
  outboundPrice: number | null;
  returnDepartureAt: string | null;
  returnAirline: string | null;
  returnPrice: number | null;
}

export interface RecommendResponse {
  results: RecommendResult[];
  restPeriods: Array<{
    id: string;
    name: string;
    start: string;
    end: string;
    type: string;
  }>;
  cachedAt: string;
  needsSetup?: boolean;
}

export const recommendApi = {
  get: () => http.get<RecommendResponse>('/api/recommend'),
};

// ========== Admin ==========

export const adminApi = {
  users: () => http.get<unknown[]>('/api/admin/users'),
  cronLogs: () => http.get<unknown[]>('/api/admin/cron-logs'),
  stats: () =>
    http.get<{
      totalUsers: number;
      recentPriceRecords: number;
      recordsBySource: Record<string, number>;
      notificationsLast7Days: number;
    }>('/api/admin/stats'),
};
